// Fictional pages/groups powering the public, unauthenticated Post Creator/Reporting demos.
// Every id/name/avatar here is entirely made up - none of it maps to a real page. The backend
// resolves real "demo" pages server-side, per network, from `network` alone (see engine's
// demoSession.js) - the frontend never knows or sends a real page id.
import { listNetworkSort } from '@/components/dash/postCreator/constante'

const NETWORKS = listNetworkSort

// Networks where the app's "conciergerie" (native session-based posting/story) mode exists at all
// - mirrors checkNatif() in store/modules/selectPage.js and isNativeInstagram/isNativeFacebook in
// postCreator/utils.js, which all gate purely on `story_config.created` (bot_status also matters
// for linkedin, already true on every fake page below).
const CONCIERGERIE_NETWORKS = ['instagram', 'facebook', 'linkedin', 'tiktok', 'twitter']

const FICTIONAL_BRANDS = [
  { id: 'aurora', name: 'Aurora Skincare', color: 'F2A6B0', address: '12 rue de la Beauté, 75002 Paris', link: 'https://aurora-skincare-demo.mashup-web.com' },
  { id: 'nomad', name: 'Nomad Coffee', color: '6B4F3B', address: '8 avenue du Café, 69002 Lyon', link: 'https://nomad-coffee-demo.mashup-web.com' }
]

// Default custom variables (store/modules/variables.js) so the "variable" picker has something
// realistic to show for the demo pages, instead of hitting the real (auth-only) /custom-variables
// endpoint and coming back empty - see fetchVariables' isDemo guard, which skips that call.
const VARIABLE_DEFS = [
  { id: 'demo-var-nom', name: 'Nom' },
  { id: 'demo-var-adresse', name: 'Adresse' },
  { id: 'demo-var-lien', name: 'Lien' }
]

const CUSTOM_BRANDS_STORAGE_KEY = 'demo/customBrands'

function avatarUrl(name, color) {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${color}&color=fff&bold=true`
}

// Shared shape for both a fictional brand page and a visitor-personalized one - real pages always
// have >=1 bot (buildPagesConvert in selectPage.js filters bots onto their page), and several
// places in postCreator/mixins/main.js read `page.bots[0].id` unguarded (e.g.
// generateDataFormRequest, called by both the preview step and saveDraft), so an empty bots array
// crashes with "Cannot read properties of undefined (reading 'id')" on every single page.
function buildPageEntry({ id, network, name, avatar, account, affectedTo }) {
  const isConciergerie = CONCIERGERIE_NETWORKS.includes(network)
  return {
    id,
    network,
    name,
    avatar,
    account,
    bot_status: true,
    story_config: { created: isConciergerie },
    // Instagram -> Facebook page association (schema field `affected_to`, engine's
    // models/schemas/page.js), needed for the Instagram story repost-to-Facebook feature.
    affected_to: affectedTo,
    // Same computation as checkNatif() (store/modules/selectPage.js) - drives the bell/concierge
    // icon shown next to a page's name in SelectPage.vue (`page.natifMode`). Hardcoding this to
    // false hid the icon on every single conciergerie-capable fake page.
    natifMode: isConciergerie,
    bots: [{
      id: `demo-bot-${id}`,
      uid: `demo-bot-${id}`,
      name,
      network,
      status: 1,
      page: { id, name, network }
    }],
    groups: []
  }
}

// Builds one full group (all networks) for a brand - shared between the 2 fictional brands and
// every visitor-personalized one, so both look and behave identically.
function buildBrandPagesAndGroup(brandId, name, avatar) {
  const pages = {}
  NETWORKS.forEach(network => {
    const id = `demo-${brandId}-${network}`
    pages[id] = buildPageEntry({
      id,
      network,
      name,
      avatar,
      account: `demo-account-${brandId}`,
      affectedTo: network === 'instagram' ? `demo-${brandId}-facebook` : undefined
    })
  })
  return { pages, group: { id: brandId, name, pages: Object.keys(pages) } }
}

// [{custom_variable, page, value}], the shape variables.js' updateValues mutation expects.
function buildVariableValues(pageIds, valuesByPageId) {
  const values = []
  pageIds.forEach(pageId => {
    const { nom, adresse, lien } = valuesByPageId(pageId)
    values.push({ custom_variable: 'demo-var-nom', page: pageId, value: nom || '' })
    values.push({ custom_variable: 'demo-var-adresse', page: pageId, value: adresse || '' })
    values.push({ custom_variable: 'demo-var-lien', page: pageId, value: lien || '' })
  })
  return values
}

/**
 * @returns {Array<{id, name, avatar, network}>} brands the visitor has personalized so far
 * (resolved via the real mention search - see PersonalizePages.vue), oldest first. Empty if they
 * never personalized anything and still see the 2 fictional brands.
 */
export function loadCustomBrands() {
  try {
    return JSON.parse(localStorage.getItem(CUSTOM_BRANDS_STORAGE_KEY)) || []
  } catch (error) {
    return []
  }
}

export function addCustomBrand(brand) {
  const brands = loadCustomBrands()
  brands.push({ id: `custom-${Date.now()}`, ...brand })
  try {
    localStorage.setItem(CUSTOM_BRANDS_STORAGE_KEY, JSON.stringify(brands))
  } catch (error) {
    console.error('[demo] failed to save personalized brand', error)
  }
  return brands
}

export function removeCustomBrand(brandId) {
  const brands = loadCustomBrands().filter(b => b.id !== brandId)
  try {
    localStorage.setItem(CUSTOM_BRANDS_STORAGE_KEY, JSON.stringify(brands))
  } catch (error) {
    console.error('[demo] failed to remove personalized brand', error)
  }
  return brands
}

/**
 * Builds the full demo page/group/variable set. As soon as the visitor has personalized at least
 * one brand, the 2 fictional brands are dropped entirely and replaced by one group per
 * personalized brand, each carrying that brand's name/avatar replicated across every network the
 * Post Creator supports (even ones we don't have a real demo page for - those stay purely
 * cosmetic, exactly like the fictional brands did) - the visitor deselects whichever pages aren't
 * relevant via the normal page selector.
 * @param {Array<{id, name, avatar}>} customBrands - loadCustomBrands()'s result
 */
export function buildDemoData(customBrands = []) {
  const brands = customBrands.length
    ? customBrands.map(b => ({ id: b.id, name: b.name, avatar: b.avatar || avatarUrl(b.name, '5C5C5C') }))
    : FICTIONAL_BRANDS

  const pages = {}
  const groups = []
  brands.forEach(brand => {
    const built = buildBrandPagesAndGroup(brand.id, brand.name, brand.avatar || avatarUrl(brand.name, brand.color || '5C5C5C'))
    Object.assign(pages, built.pages)
    groups.push(built.group)
  })

  const isFictional = brands === FICTIONAL_BRANDS
  return {
    pages,
    groups,
    variables: VARIABLE_DEFS,
    variableValues: buildVariableValues(Object.keys(pages), pageId => {
      if (!isFictional) {
        const brand = brands.find(b => pageId.startsWith(`demo-${b.id}-`))
        return { nom: brand.name, adresse: '', lien: '' }
      }
      const brand = FICTIONAL_BRANDS.find(b => pageId.startsWith(`demo-${b.id}-`))
      return { nom: brand.name, adresse: brand.address, lien: brand.link }
    })
  }
}
